import React from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';

const SECTIONS = [
  {
    title: '1. Introduction',
    body: [
      'Smart Connections provides NFC-powered digital products and business solutions including, but not limited to:',
    ],
    list: [
      'NFC Business Cards',
      'Google Review Standees',
      'TripAdvisor Review Standees',
      'Digital networking solutions',
      'Community and membership services',
    ],
    after: 'These Terms govern your use of our website and services.',
  },
  {
    title: '2. Acceptance of Terms',
    body: ['By using this website or purchasing any Smart Connections product or service, you confirm that you:'],
    list: [
      'Are at least 18 years old',
      'Agree to these Terms of Service',
      'Will use our products lawfully and responsibly',
    ],
    after: 'If you do not agree with these terms, please do not use our website or services.',
  },
  {
    title: '3. Products & Services',
    body: ['Smart Connections strives to ensure all product descriptions, pricing, and information displayed on the website are accurate. However, we reserve the right to:'],
    list: [
      'Modify products or pricing without prior notice',
      'Correct errors or inaccuracies',
      'Discontinue products or services at any time',
    ],
  },
  {
    title: '4. Orders & Payments',
    body: ['All orders are subject to acceptance and availability. By placing an order, you agree to:'],
    list: [
      'Provide accurate billing and contact information',
      'Pay all applicable fees and taxes',
      'Ensure payment authorization for the selected payment method',
    ],
    after: 'Smart Connections reserves the right to refuse or cancel any order at its discretion.',
  },
  {
    title: '5. Custom Products',
    body: ['Certain products, including custom NFC cards and branded review standees, are personalized specifically for each customer. Because of this:'],
    list: [
      'Customized products are non-refundable once production begins',
      'Customers are responsible for verifying submitted logos, text, and design details before approval',
    ],
  },
  {
    title: '6. Shipping & Delivery',
    body: ['Estimated delivery timelines are provided for convenience only and may vary due to:'],
    list: [
      'Supplier delays',
      'Shipping carrier delays',
      'Customs processing',
      'Force majeure events',
    ],
    after: 'Smart Connections is not liable for delays beyond our reasonable control.',
  },
  {
    title: '7. Refunds & Returns',
    body: ['Refunds or replacements may be offered only for:'],
    list: [
      'Defective products',
      'Incorrect items delivered',
      'Damaged products upon arrival',
    ],
    after: 'Claims must be reported within 7 days of delivery. Customized products are generally non-refundable unless defective.',
  },
  {
    title: '8. Intellectual Property',
    body: ['All content on this website, including:'],
    list: [
      'Logos',
      'Branding',
      'Graphics',
      'Product designs',
      'Website content',
      'Images and text',
    ],
    after: 'are the property of Smart Connections and may not be copied, reproduced, or distributed without written permission.',
  },
  {
    title: '9. Use of Products',
    body: ['Customers are responsible for using Smart Connections products in compliance with:'],
    list: [
      'Google policies',
      'TripAdvisor policies',
      'Applicable local laws and regulations',
    ],
    after: 'Smart Connections does not guarantee specific business results, review increases, rankings, or revenue outcomes.',
  },
  {
    title: '10. Limitation of Liability',
    body: ['To the maximum extent permitted by law, Smart Connections shall not be liable for:'],
    list: [
      'Indirect or consequential damages',
      'Loss of profits',
      'Loss of business opportunities',
      'Service interruptions',
      'Third-party platform changes or restrictions',
    ],
    after: 'Use of our products and services is at your own risk.',
  },
  {
    title: '11. Community Platform',
    body: ['Membership access and community features may be modified, updated, suspended, or discontinued at any time. Smart Connections reserves the right to:'],
    list: [
      'Remove inappropriate content',
      'Suspend abusive accounts',
      'Restrict misuse of community features',
    ],
  },
  {
    title: '12. Privacy',
    body: [
      'Your use of our services is also governed by our Privacy Policy and GDPR practices. We are committed to protecting customer information and handling data responsibly.',
    ],
  },
  {
    title: '13. Third-Party Platforms',
    body: ['Our products may integrate with third-party services including:'],
    list: ['Google', 'TripAdvisor', 'Social media platforms'],
    after: 'Smart Connections is not affiliated with or endorsed by these third-party companies unless explicitly stated.',
  },
  {
    title: '14. Governing Law',
    body: ['These Terms shall be governed and interpreted in accordance with the laws of the Cayman Islands.'],
  },
  {
    title: '15. Changes to Terms',
    body: [
      'Smart Connections reserves the right to update or modify these Terms at any time without prior notice. Continued use of the website after updates constitutes acceptance of the revised Terms.',
    ],
  },
];

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <section className="bg-navy text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <p className="uppercase tracking-widest text-yellow-400 text-sm font-semibold mb-3">
            Legal
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-4">
            Terms of Service
          </h1>
          <p className="text-white/80">Effective Date: May 2026</p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 py-16">
        <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-10 space-y-10">
          <p className="text-gray-700 leading-relaxed">
            Welcome to Smart Connections. By accessing or using our website, products, or services, you
            agree to comply with and be bound by the following Terms of Service.
          </p>

          {SECTIONS.map((sec, idx) => (
            <div key={idx} data-testid={`terms-section-${idx}`}>
              <h2 className="text-xl sm:text-2xl font-bold text-navy mb-3">{sec.title}</h2>
              {sec.body?.map((p, i) => (
                <p key={i} className="text-gray-700 leading-relaxed mb-3">
                  {p}
                </p>
              ))}
              {sec.list && (
                <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-3">
                  {sec.list.map((li, i) => (
                    <li key={i}>{li}</li>
                  ))}
                </ul>
              )}
              {sec.after && <p className="text-gray-700 leading-relaxed">{sec.after}</p>}
            </div>
          ))}

          <div className="pt-6 border-t border-gray-200">
            <h2 className="text-xl sm:text-2xl font-bold text-navy mb-3">16. Contact Information</h2>
            <p className="text-gray-700 leading-relaxed">For questions regarding these Terms, please contact:</p>
            <div className="mt-3 text-gray-700 space-y-1">
              <p className="font-semibold text-navy">Smart Connections</p>
              <p>
                <span className="text-gray-500">Web: </span>
                <a href="https://www.smartconnections.club" className="text-coral hover:underline">
                  www.smartconnections.club
                </a>
              </p>
              <p>
                <span className="text-gray-500">Email: </span>
                <a href="mailto:info@smartconnections.club" className="text-coral hover:underline">
                  info@smartconnections.club
                </a>
              </p>
              <p>
                <span className="text-gray-500">Address: </span>Grand Cayman, Cayman Islands
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
